/* eslint-disable no-console */
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import { calcularImpuesto } from "../../utils/calcularImpuesto";
import { crearOperacionFactoring, getProveedoresFactoring } from "../../utils/factoring";
import { getCuentasBancarias } from "../../utils/movimientosBancarios";

const FactoringModal = ({ isOpen, onClose, factura, onSuccess }) => {
  const { pais, user } = useAuthContext();
  const [proveedores, setProveedores] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    proveedor_factoring_id: "",
    tasa: "",
    capital_adelantado: "",
    fecha_operacion: new Date().toISOString().split("T")[0],
    fecha_cobro_final: "",
    cuenta_bancaria_id: "",
    notas: "",
  });

  useEffect(() => {
    if (!isOpen || !pais) return;
    Promise.all([getProveedoresFactoring(pais), getCuentasBancarias(pais)]).then(([provs, ctas]) => {
      setProveedores(provs);
      setCuentas(ctas);
    });
  }, [isOpen, pais]);

  useEffect(() => {
    if (factura) {
      setForm((prev) => ({
        ...prev,
        fecha_cobro_final: factura.plazocobro || "",
      }));
    }
  }, [factura]);

  const monto = Number(factura?.monto_total || 0);

  const tasa = parseFloat(form.tasa) / 100 || 0;
  const capitalAdelantado =
    form.capital_adelantado !== "" ? Number(form.capital_adelantado) : Math.round(monto * (1 - tasa));
  const costoFinanciero = monto - capitalAdelantado;
  const tasaIva = pais === "Chile" ? 0.19 : 0.18; // mantenido para mostrar el % en el label visual
  const { impuesto: ivaFinanciamiento } = calcularImpuesto(costoFinanciero, pais);

  const handleProveedorChange = (id) => {
    const prov = proveedores.find((p) => p.id === parseInt(id, 10));
    setForm((prev) => ({
      ...prev,
      proveedor_factoring_id: id,
      tasa: prov ? (prov.tasa_habitual * 100).toFixed(2) : "",
      capital_adelantado: prov ? String(Math.round(monto * (1 - prov.tasa_habitual))) : "",
    }));
  };

  const fmt = (n) => `$ ${Math.round(n).toLocaleString("es-CL")}`;

  const handleConfirmar = async () => {
    if (!form.proveedor_factoring_id) return toast.error("Seleccioná un proveedor");
    if (!form.cuenta_bancaria_id) return toast.error("Seleccioná una cuenta bancaria");
    if (!capitalAdelantado) return toast.error("Ingresá el capital adelantado");

    setLoading(true);
    try {
      await crearOperacionFactoring({
        pais,
        factura_sii_emitida_id: factura.id,
        proveedor_factoring_id: parseInt(form.proveedor_factoring_id, 10),
        monto_factura: monto,
        capital_adelantado: capitalAdelantado,
        tasa,
        costo_financiero: costoFinanciero,
        iva_financiamiento: ivaFinanciamiento,
        fecha_operacion: form.fecha_operacion,
        fecha_cobro_final: form.fecha_cobro_final || null,
        cuenta_bancaria_id: parseInt(form.cuenta_bancaria_id, 10),
        notas: form.notas || null,
        created_by: user?.id || null,
      });
      toast.success("Operación de factoring registrada");
      onSuccess?.();
      onClose();
      return null;
    } catch (err) {
      console.error(err);
      toast.error("Error al registrar el factoring");
      return null;
    } finally {
      setLoading(false);
    }
  };

  if (!factura) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>Ceder factura en factoring</ModalHeader>
      <ModalBody>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
          }}
        >
          <div>
            <strong>Factura #{factura.folio}</strong> — {factura.razon_social_receptor}
          </div>
          <div style={{ marginTop: 4, fontSize: "1rem", color: "#27ae60" }}>{fmt(monto)}</div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Proveedor de factoring
          </label>
          <select
            className="form-control form-control-sm"
            value={form.proveedor_factoring_id}
            onChange={(e) => handleProveedorChange(e.target.value)}
          >
            <option value="">Seleccionar...</option>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre} {p.tipo === "pronto_pago" ? "(pronto pago)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>Tasa (%)</label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.tasa}
            onChange={(e) => setForm((p) => ({ ...p, tasa: e.target.value, capital_adelantado: "" }))}
            placeholder="ej: 3.5"
            step="0.01"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Capital adelantado ($)
          </label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={form.capital_adelantado || capitalAdelantado}
            onChange={(e) => setForm((p) => ({ ...p, capital_adelantado: e.target.value }))}
          />
        </div>

        <div
          style={{
            background: "#fff3cd",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: "0.82rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Costo financiero:</span>
            <span style={{ color: "#c0392b" }}>{fmt(costoFinanciero)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span>
              {pais === "Chile" ? "IVA" : "IGV"} financiamiento ({Math.round(tasaIva * 100)}%):
            </span>
            <span style={{ color: "#c0392b" }}>{fmt(ivaFinanciamiento)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontWeight: 600 }}>
            <span>Total costo:</span>
            <span style={{ color: "#c0392b" }}>{fmt(costoFinanciero + ivaFinanciamiento)}</span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha operación
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_operacion}
              onChange={(e) => setForm((p) => ({ ...p, fecha_operacion: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Fecha cobro final
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={form.fecha_cobro_final}
              onChange={(e) => setForm((p) => ({ ...p, fecha_cobro_final: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Cuenta bancaria destino
          </label>
          <select
            className="form-control form-control-sm"
            value={form.cuenta_bancaria_id}
            onChange={(e) => setForm((p) => ({ ...p, cuenta_bancaria_id: e.target.value }))}
          >
            <option value="">Seleccionar...</option>
            {cuentas.map((c) => (
              <option key={c.id} value={c.id}>
                {`${c.bancos?.nombre || "Banco"} ${c.moneda} - ${c.numero_cuenta}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={form.notas}
            onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={handleConfirmar} disabled={loading}>
          {loading ? "Registrando..." : "Confirmar factoring"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FactoringModal;
